import type {
  ChatHistoryMessage,
  ClientCard,
  RepCardData,
  Source,
} from "./types";

export interface StreamCallbacks {
  onMeta?: (
    sources: Source[],
    cards: ClientCard[],
    reps: RepCardData[],
  ) => void;
  onText?: (delta: string) => void;
  onDone?: (latencyMs: number) => void;
  onError?: (message: string) => void;
}

export interface ChatRequest {
  apiUrl: string;
  clientId: string;
  sessionId: string;
  messages: ChatHistoryMessage[];
  // Only present when the visitor asked a "near me" question and granted
  // the browser location prompt (see geo.ts).
  userLocation?: { lat: number; lng: number };
}

// Fetches POST /v1/chat with manual SSE parsing. EventSource doesn't
// support POST, so we read the response body as a stream and split on
// the standard "\n\n" SSE frame delimiter ourselves.
export async function streamChat(
  req: ChatRequest,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  let resp: Response;
  try {
    resp = await fetch(`${req.apiUrl}/v1/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId: req.clientId,
        sessionId: req.sessionId,
        messages: req.messages,
        ...(req.userLocation ? { userLocation: req.userLocation } : {}),
      }),
      signal,
    });
  } catch (err) {
    callbacks.onError?.(`Network error: ${String(err)}`);
    return;
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    callbacks.onError?.(`HTTP ${resp.status}${text ? `: ${text}` : ""}`);
    return;
  }
  if (!resp.body) {
    callbacks.onError?.("No response body");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        handleFrame(frame, callbacks);
      }
    }
  } catch (err) {
    callbacks.onError?.(`Stream interrupted: ${String(err)}`);
  }
}

export interface SendTranscriptRequest {
  apiUrl: string;
  clientId: string;
  sessionId: string;
  visitorEmail: string;
  ccTeam: boolean;
  messages: ChatHistoryMessage[];
  // Bot trap. Honored by the server's /v1/email handler — non-empty
  // values silent-succeed without actually sending. Always empty in
  // legit submissions.
  honeypot?: string;
}

export type SendTranscriptResult =
  | { ok: true }
  | { ok: false; error: string };

// POST /v1/email. Plain JSON in, JSON out. The widget doesn't see the
// emails or any audit data — just success/failure.
export async function sendTranscript(
  req: SendTranscriptRequest,
): Promise<SendTranscriptResult> {
  let resp: Response;
  try {
    resp = await fetch(`${req.apiUrl}/v1/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId: req.clientId,
        sessionId: req.sessionId,
        visitorEmail: req.visitorEmail,
        ccTeam: req.ccTeam,
        messages: req.messages,
        _hp: req.honeypot ?? "",
      }),
    });
  } catch (err) {
    return { ok: false, error: `Network error: ${String(err)}` };
  }

  if (resp.status === 429) {
    return { ok: false, error: "Too many requests. Try again in a minute." };
  }
  if (resp.status === 403) {
    return { ok: false, error: "This site isn't allowed to send transcripts." };
  }
  if (resp.status === 503) {
    return { ok: false, error: "Email sending is not configured." };
  }
  if (!resp.ok) {
    return { ok: false, error: `Send failed (HTTP ${resp.status}).` };
  }
  return { ok: true };
}

// Fire-and-forget conversion event. Best-effort like fetchCards — never
// throws into the UI. `keepalive` lets the POST complete even when the click
// that triggered it navigates the tab away (plan/source links open _blank, but
// belt-and-suspenders for same-tab cases).
export async function sendEvent(
  apiUrl: string,
  clientId: string,
  sessionId: string,
  type: string,
  ref?: string,
  metadata?: Record<string, unknown>,
  source?: "widget" | "bridge" | "attribution",
): Promise<void> {
  try {
    await fetch(`${apiUrl}/v1/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId, sessionId, type, ref, metadata, source }),
      keepalive: true,
    });
  } catch {
    // Swallow — conversion tracking must never disrupt the visitor.
  }
}

export async function fetchCards(
  apiUrl: string,
  clientId: string,
): Promise<ClientCard[]> {
  try {
    const resp = await fetch(
      `${apiUrl}/v1/cards/${encodeURIComponent(clientId)}`,
    );
    if (!resp.ok) return [];
    const json = (await resp.json()) as { cards?: ClientCard[] };
    return Array.isArray(json.cards) ? json.cards : [];
  } catch {
    return [];
  }
}

function handleFrame(frame: string, callbacks: StreamCallbacks): void {
  let eventType = "message";
  let dataStr = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) eventType = line.slice(6).trim();
    else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
    // Lines starting with ":" are comments / keepalives — ignore.
  }
  if (!dataStr) return;

  let data: any;
  try {
    data = JSON.parse(dataStr);
  } catch {
    return;
  }

  switch (eventType) {
    case "meta":
      callbacks.onMeta?.(data.sources ?? [], data.cards ?? [], data.reps ?? []);
      break;
    case "text":
      callbacks.onText?.(data.delta ?? "");
      break;
    case "done":
      callbacks.onDone?.(data.latency_ms ?? 0);
      break;
    case "error":
      callbacks.onError?.(data.message ?? "stream error");
      break;
  }
}
